import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from './ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from './ui/select';
import { Search, Printer, FileText, Trash2, Download } from 'lucide-react';
import { toast } from 'sonner';
import { API_BASE as API_URL } from '../lib/apiBase';
import { printReport, downloadFile, sortRows } from '../lib/printReport';

const getHeaders = () => {
  const token = localStorage.getItem('hrms-token');
  return token ? { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' } : {};
};

const STATUS_BADGE = {
  approved: 'bg-green-100 text-green-700',
  pending: 'bg-amber-100 text-amber-700',
  rejected: 'bg-red-100 text-red-700',
  cancelled: 'bg-slate-100 text-slate-500',
};

const LEAVE_TYPES = ['Casual Leave', 'Earned Leave', 'Medical Leave', 'Maternity Leave',
  'Paternity Leave', 'Study Leave', 'Academic Leave', 'On-Duty', 'Vacation Leave', 'Half Day', 'Compensatory Off'];

export const LeaveRecordsReport = ({ entityId, canEdit, onEdit, onDelete, refreshKey }) => {
  const [data, setData] = useState({ rows: [], departments: [], categories: [] });
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('approved');
  const [dept, setDept] = useState('all');
  const [category, setCategory] = useState('all');
  const [leaveType, setLeaveType] = useState('all');
  const [month, setMonth] = useState('');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('date_desc');

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ status, department: dept, category, leave_type: leaveType });
      if (entityId) params.append('entity_id', entityId);
      if (month) params.append('month', month);
      const res = await fetch(`${API_URL}/api/leaves/records-report?${params}`, { headers: getHeaders(), credentials: 'include' });
      if (res.ok) setData(await res.json());
    } catch { toast.error('Failed to load leave records'); }
    finally { setLoading(false); }
  }, [entityId, status, dept, category, leaveType, month, refreshKey]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const rows = sortRows((data.rows || []).filter(r => {
    const s = search.toLowerCase();
    return !s || (r.employee_name || '').toLowerCase().includes(s) || (r.employee_id || '').toLowerCase().includes(s);
  }), sort, 'start_date');

  const buildParams = (extra = {}) => {
    const params = new URLSearchParams({ status, department: dept, category, leave_type: leaveType, sort, ...extra });
    if (entityId) params.append('entity_id', entityId);
    if (month) params.append('month', month);
    return params;
  };

  const handleExcel = async () => {
    try { await downloadFile(`${API_URL}/api/leaves/records-report?${buildParams({ format: 'excel' })}`, 'leave_records.xlsx', getHeaders()); }
    catch (e) { toast.error(e.message); }
  };

  const handlePrint = () => {
    printReport(
      'Leave Records Report',
      `Status: ${status} · ${dept !== 'all' ? `Dept: ${dept} · ` : ''}${category !== 'all' ? `Category: ${category} · ` : ''}${leaveType !== 'all' ? `Type: ${leaveType} · ` : ''}${month ? `Month: ${month} · ` : ''}${rows.length} record(s)`,
      ['#', 'Employee', 'Department', 'Category', 'Leave Type', 'From', 'To', 'Days', 'Reason', 'Status'],
      rows.map((r, i) => [i + 1, `${r.employee_name} (${r.employee_id})`, r.department, r.category,
        r.leave_type, r.start_date, r.end_date, r.days, r.reason, r.status])
    );
  };

  return (
    <div className="space-y-4" data-testid="leave-records-report">
      <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
        <div className="relative col-span-2 md:col-span-2">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input placeholder="Search employee..." className="pl-10" value={search}
            onChange={e => setSearch(e.target.value)} data-testid="lr-search" />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid="lr-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
            <SelectItem value="all">All statuses</SelectItem>
          </SelectContent>
        </Select>
        <Select value={dept} onValueChange={setDept}>
          <SelectTrigger data-testid="lr-dept"><SelectValue placeholder="Department" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All departments</SelectItem>
            {data.departments.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger data-testid="lr-category"><SelectValue placeholder="Category" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All categories</SelectItem>
            {data.categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={leaveType} onValueChange={setLeaveType}>
          <SelectTrigger data-testid="lr-type"><SelectValue placeholder="Leave type" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            {LEAVE_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="flex items-center gap-3 flex-wrap">
        <Input type="month" className="w-44" value={month} onChange={e => setMonth(e.target.value)} data-testid="lr-month" />
        {month && <Button variant="ghost" size="sm" onClick={() => setMonth('')}>Clear month</Button>}
        <Select value={sort} onValueChange={setSort}>
          <SelectTrigger className="w-44" data-testid="lr-sort"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="date_desc">Date (newest first)</SelectItem>
            <SelectItem value="date_asc">Date (oldest first)</SelectItem>
            <SelectItem value="name_asc">Name (A–Z)</SelectItem>
            <SelectItem value="name_desc">Name (Z–A)</SelectItem>
            <SelectItem value="department">Department</SelectItem>
          </SelectContent>
        </Select>
        <Badge variant="outline" className="border-slate-300">{rows.length} record(s)</Badge>
        <Button variant="outline" className="ml-auto" onClick={handleExcel} disabled={rows.length === 0} data-testid="lr-excel-btn">
          <Download className="w-4 h-4 mr-1.5" />Excel
        </Button>
        <Button variant="outline" onClick={handlePrint} disabled={rows.length === 0} data-testid="lr-print-btn">
          <Printer className="w-4 h-4 mr-1.5" />Print
        </Button>
      </div>

      {loading ? (
        <div className="space-y-3">{[...Array(4)].map((_, i) => <div key={i} className="h-12 bg-slate-100 rounded animate-pulse" />)}</div>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Employee</TableHead>
                    <TableHead>Department</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Leave Type</TableHead>
                    <TableHead>Dates</TableHead>
                    <TableHead className="text-center">Days</TableHead>
                    <TableHead>Reason</TableHead>
                    <TableHead>Status</TableHead>
                    {canEdit && <TableHead className="text-right">Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map(r => (
                    <TableRow key={r.leave_id} data-testid={`lr-row-${r.leave_id}`}>
                      <TableCell>
                        <p className="text-sm font-medium">{r.employee_name}</p>
                        <code className="text-[10px] text-slate-400">{r.employee_id}</code>
                      </TableCell>
                      <TableCell className="text-xs">{r.department || '—'}</TableCell>
                      <TableCell className="text-xs">{r.category || '—'}</TableCell>
                      <TableCell><Badge variant="outline" className="text-xs">{r.leave_type}</Badge></TableCell>
                      <TableCell className="text-xs">{r.start_date} → {r.end_date}</TableCell>
                      <TableCell className="text-center text-sm font-semibold">
                        {r.days}
                        {r.partial_approval && <p className="text-[9px] text-amber-600">{r.paid_days}p/{r.unpaid_days}LOP</p>}
                      </TableCell>
                      <TableCell><p className="max-w-[160px] truncate text-xs text-slate-500" title={r.reason}>{r.reason}</p></TableCell>
                      <TableCell><span className={`text-xs px-2 py-0.5 rounded ${STATUS_BADGE[r.status] || 'bg-slate-100'}`}>{r.status}</span></TableCell>
                      {canEdit && (
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button size="sm" variant="outline" className="text-blue-600 hover:bg-blue-50 h-7 px-2"
                              onClick={() => onEdit(r)} data-testid={`lr-edit-${r.leave_id}`}>
                              <FileText className="w-3.5 h-3.5 mr-1" />Edit
                            </Button>
                            <Button size="sm" variant="ghost" className="text-slate-400 hover:text-red-600 h-7 px-2"
                              onClick={() => onDelete(r.leave_id)} data-testid={`lr-delete-${r.leave_id}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                  {rows.length === 0 && (
                    <TableRow><TableCell colSpan={canEdit ? 9 : 8} className="text-center py-10 text-slate-500 text-sm">No leave records match the filters</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
